import React, { useState, useEffect, useMemo } from 'react'
import {
    Alert,
    Divider,
    Grid,
    Select,
    MenuItem,
    Box,
    LinearProgress,
} from '@mui/material'
import { RRule, Options } from 'rrule'

import DisplayItemFilter from '@/Components/DisplayItems/Filter/DisplayItemFilter'
import moment from 'moment'
import AppDialog from '@/Components/AppDialog/AppDialog'
import { StyledButton } from '@/Components/Elements/StyledComponents/Buttons'
import {
    getRecurrentOptions,
    getShortDays,
    getShortMonths,
    isValidNumericInput,
} from './utils'
import StartDateSection from './StartDateSection'
import RecurrenceForm from './RecurrenceForm'
import EndDateSelector from './EndDateSelector'
import { MultiAddProps } from './types'
import { FormInputLabel } from '@/Components/Elements/StyledComponents/Input'

const ShowMultiAdd = ({
    kedo,
    handleGetValues,
    displayItems,
    handleClose,
    handleSuccess,
    defDossier,
    linkId,
    embedded,
}: MultiAddProps) => {
    const [item, setItem] = useState(null)
    const [currentItem, setCurrentItem] = useState(null)
    const [curPeriod, setCurPeriod] = useState('daily')
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [days, setDays] = useState<string[]>([])
    const [dates, setDates] = useState<Date[]>([])
    const [totalItems, setTotalItems] = useState(0)
    const [done, setDone] = useState(0)
    const [submitting, setSubmitting] = useState(false)
    const [success, setSuccess] = useState(false)
    const [errors, setErrors] = useState<string[]>([])
    const [mode, setMode] = useState('BYMONTHDAY')
    const [byMonth, setByMonth] = useState('1')
    const [byMonthDay, setByMonthDay] = useState('1')
    const [bySetPos, setBySetPos] = useState(null)
    const [endMode, setEndMode] = useState('date')
    const [afterExecutions, setAfterExecutions] = useState(1)
    const [intervaling, setIntervaling] = useState(1)

    const recurrent = useMemo(() => getRecurrentOptions(kedo), [kedo])
    const shortDays = useMemo(() => getShortDays(kedo), [kedo])
    const shortMonths = useMemo(() => getShortMonths(kedo), [kedo])

    useEffect(() => {
        const values = handleGetValues()
        if (values[item?.id]) {
            const start = new Date(values[item?.id])
            let end
            if (curPeriod === 'monthly') {
                end = new Date(start)
                end.setFullYear(start.getFullYear() + 1)
            } else {
                end = new Date()
                end.setDate(start.getDate() + 1)
            }
            setStartDate(start)
            setEndDate(end)
        } else {
            const start = new Date()
            let end
            if (curPeriod === 'monthly') {
                end = new Date(start)
                end.setFullYear(start.getFullYear() + 1)
            } else {
                end = new Date()
            }
            setStartDate(start)
            setEndDate(end)
        }
    }, [item, curPeriod])

    useEffect(() => {
        setErrors([])
        if (!startDate || !endDate) return
        setDateRules()
    }, [
        days,
        startDate,
        endDate,
        intervaling,
        afterExecutions,
        byMonthDay,
        byMonth,
        bySetPos,
    ])
    const handleRecurChange = (value: string) => {
        if (!['daily', 'weekly'].includes(value)) {
            setDays([])
        }

        switch (value) {
            case 'yearly':
                setMode('BYMONTH')
                setByMonthDay('1')
                setByMonth('1')
                break
            case 'monthly':
                setMode('BYMONTHDAY')
                setByMonthDay('1')
                break
        }

        if (value === '') {
            setCurPeriod(null)
            return
        }

        setCurPeriod(value)
    }

    const handleEndChange = (e) => {
        if (e.target.value === '') {
            setEndMode(null)
            return
        }

        setEndMode(e.target.value)
    }

    const changeDay = (e): void => {
        if (curPeriod === 'monthly') {
            setDays(e.target.value.split(','))
        } else {
            let newDays = [...days]
            newDays = newDays.filter((item) => item !== e.target.value)

            if (e.target.checked) {
                newDays.push(e.target.value)
            }

            setDays(newDays)
        }
    }

    const getFreq = (period: string) => {
        switch (period) {
            case 'daily':
                return RRule.DAILY
            case 'yearly':
                return RRule.YEARLY
            case 'monthly':
                return RRule.MONTHLY
            case 'weekly':
                return RRule.WEEKLY
            default:
                return RRule.MONTHLY
        }
    }

    const setDateRules = () => {
        const weekDays = []
        const errorsArray: string[] = []

        if (days.includes('Sunday')) weekDays.push(RRule.SU)
        if (days.includes('Monday')) weekDays.push(RRule.MO)
        if (days.includes('Tuesday')) weekDays.push(RRule.TU)
        if (days.includes('Wednesday')) weekDays.push(RRule.WE)
        if (days.includes('Thursday')) weekDays.push(RRule.TH)
        if (days.includes('Friday')) weekDays.push(RRule.FR)
        if (days.includes('Saturday')) weekDays.push(RRule.SA)

        const dtstart = startDate ? new Date(startDate) : null

        if (dtstart && dtstart.getHours() === 0 && dtstart.getMinutes() === 0) {
            // date-only input → fix to safe hour
            dtstart.setHours(9, 0, 0, 0)
        }

        const data = {
            interval: intervaling,
            freq: getFreq(curPeriod),
            dtstart: dtstart,
        } as Options

        if (weekDays.length > 0) {
            data.byweekday = weekDays
        }

        if (mode === 'BYSETPOS') {
            data.bysetpos = +bySetPos

            if (curPeriod === 'yearly') {
                data.bymonth = parseInt(byMonth)
            }
        }

        if (mode === 'BYMONTHDAY' && weekDays.length === 0) {
            data.bymonthday = parseInt(byMonthDay)
        }

        if (mode === 'BYMONTH' && weekDays.length === 0) {
            data.bymonthday = parseInt(byMonthDay)
            data.bymonth = parseInt(byMonth)
        }

        if (endMode === 'after') {
            data.count = afterExecutions
        }

        if (endMode === 'date') {
            data.until = endDate
        }

        const rule = new RRule(data)

        const all = rule
            .all()
            .map(
                (d) =>
                    new Date(
                        d.getFullYear(),
                        d.getMonth(),
                        d.getDate(),
                        0,
                        0,
                        0,
                        0
                    )
            )
        setErrors(errorsArray)
        setDates(all)
    }

    const changeMonthMode = (e): void => {
        if (e.target.name !== mode) {
            setDays([])
            setBySetPos(null)
            setByMonthDay(null)
            setByMonth(null)
            setMode(e.target.name)
            initMonthMode(e.target.name)
        }
    }

    const initMonthMode = (mode): void => {
        if (mode === 'BYMONTH') {
            setByMonthDay('1')
            setByMonth('1')
        }

        if (mode === 'BYMONTHDAY') {
            setByMonthDay('1')
        }

        if (mode === 'BYSETPOS') {
            setBySetPos('1')
            setDays(['Monday'])

            if (curPeriod === 'yearly') {
                setByMonth('1')
            }
        }
    }

    const translateDays = (period) => {
        switch (period) {
            case 'daily':
                return 'Day'
            case 'yearly':
                return 'year'
            case 'monthly':
                return 'Month'
            case 'weekly':
                return 'Week'
            default:
                return ''
        }
    }

    const isValidStartDate = (current) => {
        if (
            item?.def_dossier_def_field?.def_field?.settings?.restriction ===
            'before_and_eq_current_date'
        ) {
            return current.isSameOrBefore(moment())
        } else if (
            item?.def_dossier_def_field?.def_field?.settings?.restriction ===
            'after_and_eq_current_date'
        ) {
            return current.isSameOrAfter(moment())
        } else {
            return current
        }
    }

    const isValidEndDate = (current) => {
        if (
            item?.def_dossier_def_field?.def_field?.settings?.restriction ===
            'before_and_eq_current_date'
        ) {
            return current.isSameOrBefore(moment())
        } else if (
            item?.def_dossier_def_field?.def_field?.settings?.restriction ===
            'after_and_eq_current_date'
        ) {
            return current.isSameOrAfter(moment())
        } else {
            return current >= moment(startDate).add(-1, 'days')
        }
    }

    const submitDossier = async (date) => {
        const values = handleGetValues()
        values['defDossier'] = defDossier.id
        values['linkId'] = linkId
        values['embedded'] = embedded
        const itemId = item.id

        const localDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            curPeriod === 'monthly' ? 0 : date.getHours(),
            curPeriod === 'monthly' ? 0 : date.getMinutes(),
            curPeriod === 'monthly' ? 0 : date.getSeconds()
        )

        const year = localDate.getFullYear()
        const month = String(localDate.getMonth() + 1).padStart(2, '0')
        const day = String(localDate.getDate()).padStart(2, '0')
        const hours = String(localDate.getHours()).padStart(2, '0')
        const minutes = String(localDate.getMinutes()).padStart(2, '0')
        const seconds = String(localDate.getSeconds()).padStart(2, '0')

        values[
            itemId
        ] = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
        setCurrentItem(date.toLocaleString())

        const response = await kedo
            .api()
            .post(kedo.api().getContentDossierEndpoint(), values)
    }

    const submitDossiers = async () => {
        const errorsArray = []

        if (!isValidStartDate(moment(startDate))) {
            errorsArray.push(kedo.t('end_or_start_date_future'))
        }

        if (!isValidEndDate(moment(endDate))) {
            errorsArray.push(kedo.t('end_date_out_of_range'))
        }

        if (errorsArray.length > 0) {
            setErrors(errorsArray)
            return
        }

        if (dates.length <= 0) {
            return
        }

        setTotalItems(dates.length)
        setSubmitting(true)

        const forLoop = async () => {
            for (let index = 0; index < dates.length; index++) {
                await submitDossier(dates[index])
                setDone(index + 1)
            }
        }
        await forLoop()
        setSuccess(true)
        setTimeout(() => {
            setSubmitting(false)
            handleSuccess()
        }, 2000)
    }

    const getCurrentLocale = () => {
        const locale = kedo.env().getSelectedLocale()
        if (locale && locale.code) {
            return locale.code.replace('_', '-')
        }

        return 'en'
    }

    const renderItem = () => {
        if (!item) {
            return
        }

        if (item?.def_dossier_def_field?.def_field?.type !== 'date') {
            return null
        }
        return (
            <>
                <div className="container">
                    <StartDateSection
                        startDate={startDate}
                        setStartDate={setStartDate}
                        submitting={submitting}
                        isValidStartDate={isValidStartDate}
                        getCurrentLocale={getCurrentLocale}
                        label={kedo.t('Start date')}
                    />

                    <Divider sx={{ mb: 2 }} />
                    <RecurrenceForm
                        kedo={kedo}
                        recurrent={recurrent}
                        curPeriod={curPeriod}
                        handleRecurChange={handleRecurChange}
                        shortDays={shortDays}
                        days={days}
                        setDays={setDays}
                        changeDay={changeDay}
                        mode={mode}
                        changeMonthMode={changeMonthMode}
                        byMonthDay={byMonthDay}
                        setByMonthDay={setByMonthDay}
                        bySetPos={bySetPos}
                        setBySetPos={setBySetPos}
                        shortMonths={shortMonths}
                        byMonth={byMonth}
                        setByMonth={setByMonth}
                        startDate={startDate}
                        intervaling={intervaling}
                        setIntervaling={setIntervaling}
                        isValidNumericInput={isValidNumericInput}
                        translateDays={translateDays}
                    />
                    <Divider sx={{ mb: 2 }} />
                    <EndDateSelector
                        kedo={kedo}
                        endMode={endMode}
                        handleEndChange={handleEndChange}
                        afterExecutions={afterExecutions}
                        setAfterExecutions={setAfterExecutions}
                        isValidNumericInput={isValidNumericInput}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        submitting={submitting}
                        getCurrentLocale={getCurrentLocale}
                        isValidEndDate={isValidEndDate}
                    />
                </div>
            </>
        )

        return
    }

    const renderAlert = () => {
        if (item?.def_dossier_def_field?.def_field?.settings?.restriction) {
            const restriction =
                item.def_dossier_def_field.def_field.settings.restriction

            if (restriction === 'before_and_eq_current_date') {
                return (
                    <Alert severity="warning">{`${kedo.t(
                        'Before and equal to the current date'
                    )}: ${kedo.t('Enabled')}`}</Alert>
                )
            } else if (restriction === 'after_and_eq_current_date') {
                return (
                    <Alert severity="warning">{`${kedo.t(
                        'After and equal to the current date'
                    )}: ${kedo.t('Enabled')}`}</Alert>
                )
            }
        }
    }

    return (
        <AppDialog
            aria-labelledby="contained-modal-title-vcenter"
            open
            maxWidth={'lg'}
            fullWidth
            topAlign
            title={`${kedo.t('Multiple')} ${String(
                kedo.env().translateItem(defDossier, 'defdossier', true)
            ).toLowerCase()}`}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    handleClose()
                }
            }}
            headerClose={handleClose}
            contentProps={{ dividers: true }}
            actions={
                <>
                    {item && (
                        <StyledButton
                            disabled={submitting}
                            onClick={submitDossiers}
                        >
                            {kedo.t('Save')}
                        </StyledButton>
                    )}
                    <StyledButton
                        disabled={submitting}
                        onClick={handleClose}
                        color={'secondary'}
                    >
                        {kedo.t('Close')}
                    </StyledButton>
                </>
            }
        >
            <div>
                {item && renderAlert()}
                {!item && (
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ md: 12 }}>
                            <FormInputLabel>
                                {kedo.t(
                                    'Choose a field to create items by a set'
                                )}
                            </FormInputLabel>
                        </Grid>
                        <Grid size={{ md: 12 }}>
                            <Select
                                fullWidth
                                size="small"
                                displayEmpty
                                value={item?.id || ''}
                                onChange={(e) => {
                                    setItem(
                                        displayItems.find(
                                            (ddiItem) =>
                                                ddiItem.id ===
                                                parseInt(
                                                    e.target.value as string
                                                )
                                        )
                                    )
                                }}
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>
                                {DisplayItemFilter.byTypes(displayItems, [
                                    'date',
                                    'datetime',
                                ]).map((displayItem) => (
                                    <MenuItem
                                        value={displayItem.id}
                                        key={displayItem.id}
                                    >
                                        {kedo.translateItem(
                                            displayItem,
                                            'displayitem'
                                        )}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>
                    </Grid>
                )}
                {item && renderItem()}
                {submitting && !success && (
                    <Box textAlign="center">
                        <span>{currentItem}</span>
                    </Box>
                )}
                {submitting && !success && (
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress
                            variant="determinate"
                            value={totalItems ? (done / totalItems) * 100 : 0}
                        />
                    </Box>
                )}
                {success && (
                    <Alert severity={'success'}>
                        {kedo.t('Item has been saved')}
                    </Alert>
                )}
                {errors && errors.length > 0 && (
                    <Alert severity={'error'}>
                        {errors.map((error, index) => (
                            <>
                                <span key={index}>{error}</span>
                                <br />
                            </>
                        ))}
                    </Alert>
                )}
            </div>
        </AppDialog>
    )
}

export default ShowMultiAdd
